import { faFileLines } from '@fortawesome/pro-light-svg-icons';
import { faDollarSign } from '@fortawesome/pro-regular-svg-icons';
import { Flex, Stack, useMantineTheme } from '@mantine/core';
import classNames from 'classnames';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TRedemptionType } from '@feathr/blackbox';
import { Heading, Icon, Pill, Skeleton, Text, Well } from '@feathr/components';

import * as styles from './RedemptionTypeSelect.css';

export interface IRedemptionTypeSelectProps {
  /** Available credits to display */
  availableCredits: number;
  /** Whether credits are being loaded */
  isLoading?: boolean;
  /** Callback when a redemption type is selected (also handles navigation) */
  onSelect: (type: TRedemptionType) => void;
}

/**
 * Step 1: Allows the user to select how they want to redeem their credits.
 * Displays two clickable cards for Media Balance and Invoice Payment options.
 */
function RedemptionTypeSelect({
  availableCredits,
  isLoading = false,
  onSelect,
}: Readonly<IRedemptionTypeSelectProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    spacing: { '1': cardContentGap, '2': cardsGap, '4': sectionGap, xs: pillGap },
  } = useMantineTheme();

  function handleMediaBalanceClick(): void {
    onSelect('media');
  }

  return (
    <Stack gap={sectionGap}>
      <Well className={styles.availableCredits} theme={'blue'}>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {t('Available Credits')}
        </Text.DefaultHeadingMedium>
        <Text.DefaultHeadingMedium className={styles.availableCreditsText}>
          {isLoading ? <Skeleton width={60} /> : availableCredits.toLocaleString()}
        </Text.DefaultHeadingMedium>
      </Well>

      <Stack gap={cardsGap}>
        <Well
          borderless={true}
          className={styles.card}
          name={'media-balance-option'}
          onClick={handleMediaBalanceClick}
        >
          <div className={styles.iconWrapper}>
            <Icon className={styles.icon} icon={faDollarSign} size={8} />
          </div>
          <Stack className={styles.cardContent} gap={cardContentGap}>
            <Heading mantineComponent={'span'} variant={'h5'}>
              {t('Media Balance')}
            </Heading>
            <Text.DefaultRegular truncate={false}>
              {t("Add funds to a project's prepaid media balance to fund advertising campaigns")}
            </Text.DefaultRegular>
          </Stack>
        </Well>

        <Well
          aria-disabled={'true'}
          borderless={true}
          className={classNames(styles.card, styles.cardDisabled)}
          name={'invoice-payment-option'}
        >
          <div className={styles.iconWrapper}>
            <Icon className={styles.icon} icon={faFileLines} size={8} />
          </div>
          <Stack className={styles.cardContent} gap={cardContentGap}>
            <Flex align={'center'} gap={pillGap}>
              <Heading mantineComponent={'span'} variant={'h5'}>
                {t('Invoice Payment')}
              </Heading>
              <Pill showPrefix={false} theme={'yellow'}>
                {t('Coming Soon')}
              </Pill>
            </Flex>
            <Text.DefaultRegular truncate={false}>
              {t('Apply credits toward an outstanding invoice')}
            </Text.DefaultRegular>
          </Stack>
        </Well>
      </Stack>
    </Stack>
  );
}

export default RedemptionTypeSelect;
